import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useHashScroll } from '../lib/useHashScroll';
import { SiteFooter } from '../components/SiteFooter';
import { CONTACT } from '../lib/contact';
import { PublicHeader } from '../components/PublicHeader';
import { HeroGlow } from '../components/HeroGlow';
import { GLOW_ENABLED } from '../lib/siteEffects';
import { PortfolioFilm } from '../components/CinematicBits';
import { Marquee } from '../components/Marquee';
import { ArrearsAnswer } from '../components/ArrearsAnswer';
import { ProductTour, type TourStop } from '../components/ProductTour';
import { HeroTicker, WatchConsole } from '../components/WatchConsole';
import { HeroMedia } from '../components/HeroMedia';
import { SbIcon } from '../components/sidebarIcons';
import { useApi } from '../lib/useApi';
import { useCanonical } from '../lib/canonical';
import { AGENT_COUNT, CATEGORY_LABEL, COLUMNS, WORKFORCE, agentsIn } from '../lib/aiWorkforce';
import {
  FALLBACK_MODEL, bandLabel, bandSum, bandTotal, formatKES, planLadder, quoteUnits, rateRows,
  type PricingResponse,
} from '../lib/pricing';
import '../styles/landing.css';

/*
 * The home tour: nine real screens, one per tab, chosen by the visitor.
 *
 * NOT DRAWN IN MARKUP. What stood here once was a dashboard built in JSX with
 * figures typed in by hand: edited every time the demo portfolio moved, never
 * quite the product, and asking a visitor to take our word for what the software
 * looks like on the strength of a picture we drew of it. Each image is cropped to
 * the content pane in the FILE (no sidebar), and the demo portfolio's figures are
 * the only figures on them. The owner portal sits in the list because it is the
 * part no competitor at this price has. Per-property rows in those screens are
 * blurred in the file: the headline cards carry the point.
 */
const TOUR: TourStop[] = [
  { key: 'dashboard', tab: 'Dashboard', icon: 'dashboard', line: 'Occupancy, what is due and what came in, on one screen.',
    src: '/screens/dashboard.webp?v=2', alt: 'The NyumbaSuite dashboard showing properties, units, occupancy, net income and rent collected' },
  { key: 'rent', tab: 'Rent and payments', icon: 'rent', line: 'Expected, collected and outstanding rent, tenant by tenant.',
    src: '/screens/rent-payments.webp?v=2', alt: 'The rent and payments screen showing expected, collected and outstanding rent with the charges behind it' },
  { key: 'financials', tab: 'Financials', icon: 'reports', line: 'Money in, money out and what is still owed, for any period.',
    src: '/screens/financials.webp?v=2', alt: 'The financials screen showing money in, money out, net operating income, collection rate and what is still owed' },
  { key: 'invoices', tab: 'Invoices', icon: 'invoices', line: 'Billed, paid, still owed and overdue, invoice by invoice.',
    src: '/screens/invoices.webp?v=2', alt: 'The invoices screen showing total invoiced, paid, outstanding and overdue, with the invoices behind them' },
  { key: 'utilities', tab: 'Utilities and meters', icon: 'utilities', line: 'Water and electricity read per unit, ready to bill on.',
    src: '/screens/utilities.webp?v=2', alt: 'The utility readings screen showing water and electricity consumption per meter' },
  { key: 'reports', tab: 'Reports and KRA', icon: 'reports', line: 'Twenty-one reports, including the Monthly Rental Income summary.',
    src: '/screens/reports.webp?v=2', alt: 'The reports screen listing the reports available, each downloadable as a spreadsheet' },
  { key: 'agents', tab: 'AI agents', icon: 'intelligence', line: 'Specialist agents watch your portfolio and say what needs doing first.',
    src: '/screens/ai-agents.webp?v=2', alt: 'The What the AI found screen showing critical and attention counts, findings by severity and by specialist agent, and the findings behind them' },
  { key: 'tenants', tab: 'Tenant portal', icon: 'tenants', line: 'Tenants check their own balance and raise repairs.',
    src: '/screens/tenant-portal.webp?v=2', alt: 'The tenant portal showing a tenant their own balance, statements and documents' },
  { key: 'owners', tab: 'Owner portal', icon: 'owners', line: 'Each owner signs in to see only the properties they own.',
    src: '/screens/owner-portfolio.webp?v=2', alt: 'The owner portal showing a property owner their own properties, units, occupancy, rent collected and net operating income' },
  { key: 'leases', tab: 'Leases', icon: 'leases', line: 'Lease documents with renewal reminders, kept with the tenancy.',
    src: '/screens/leases.webp?v=2', alt: 'The leases screen showing lease documents and renewal dates' },
];

/** The strip under the hero: the same real screens, moving. */
const STRIP: { src: string; alt: string; label: string }[] = [
  { src: '/screens/dashboard.webp?v=2', alt: 'Dashboard', label: 'Dashboard' },
  { src: '/screens/rent-payments.webp?v=2', alt: 'Rent and payments', label: 'Rent and payments' },
  { src: '/screens/financials.webp?v=2', alt: 'Financials', label: 'Financials' },
  { src: '/screens/invoices.webp?v=2', alt: 'Invoices', label: 'Invoices' },
  { src: '/screens/ai-agents.webp?v=2', alt: 'AI agents', label: 'AI agents' },
  { src: '/screens/tenant-portal.webp?v=2', alt: 'Tenant portal', label: 'Tenant portal' },
  { src: '/screens/maintenance.webp?v=2', alt: 'Maintenance', label: 'Maintenance' },
  { src: '/screens/owner-portfolio.webp?v=2', alt: 'Owner portal', label: 'Owner portal' },
  { src: '/screens/utilities.webp?v=2', alt: 'Utilities', label: 'Utilities' },
];

/**
 * The property strip. Captions describe the kind of building, not a named client:
 * these are illustrations of the homes the product manages. Swap a file name here
 * to change a card.
 */
const PLACES: { src: string; alt: string; label: string; sub: string }[] = [
  { src: '/hero-building.webp', alt: 'A modern apartment building in Nairobi at golden hour', label: 'Apartments', sub: 'Balconies and a landscaped garden' },
  { src: '/estate-townhouses.webp', alt: 'A row of modern townhouses in a gated estate', label: 'Gated estate', sub: 'Townhouses on a shared drive' },
  { src: '/explore/estate-2.webp', alt: 'A modern four-storey apartment block on a Nairobi street corner', label: 'Apartment block', sub: 'Four floors, street corner' },
  { src: '/showcase/showcase-manager.webp', alt: 'A property manager smiling at a tablet in an upmarket apartment lobby', label: 'Property managers', sub: 'Checking the building from the lobby' },
  { src: '/explore/premium-1.webp', alt: 'A glass and stone residential tower beside a pond', label: 'Residential tower', sub: 'Glass, stone and water' },
  { src: '/explore/premium-3.webp', alt: 'A rooftop pool and sun deck above the city at sunset', label: 'Rooftop pool', sub: 'City view at sunset' },
  { src: '/showcase/showcase-landlord.webp', alt: 'A landlord checking his phone on a balcony above the Nairobi skyline at golden hour', label: 'Landlords', sub: 'Rent in hand before you knock' },
  { src: '/explore/estate-4.webp', alt: 'A row of modern maisonettes in a gated compound', label: 'Maisonettes', sub: 'Gated compound' },
  { src: '/explore/premium-4.webp', alt: 'A bright residential lobby with a large plant', label: 'Lobby', sub: 'Marble floors, natural light' },
  { src: '/explore/estate-5.webp', alt: 'A high-end gated development seen from its landscaped driveway', label: 'Gated development', sub: 'Landscaped driveway' },
  { src: '/images/sol-building.webp', alt: 'A residential block at dusk with lit windows', label: 'Mid-rise at dusk', sub: 'Lit and lived in' },
  { src: '/images/sol-estate.webp', alt: 'A gated townhouse estate with a security gatehouse', label: 'Estate gate', sub: 'Gatehouse and curved drive' },
];

/*
 * The connected-platform hub: our modules on a wheel around one core, with a
 * description panel that tracks whichever node you point at (and the reverse).
 *
 * Everything here is in the product today except the one node marked `soon`
 * (eTIMS), which is drawn as not-yet rather than quietly implied to be live —
 * the same rule the "Coming soon" markers follow across the rest of the site.
 */
const HUB: { ic: string; icon: string; label: string; name: string; desc: string; soon?: true }[] = [
  { ic: 'ic-teal', icon: 'properties', label: 'Properties', name: 'Properties & units', desc: 'Every property and unit, occupancy and vacancy at a glance, scoped so staff see only what you allow.' },
  { ic: 'ic-blue', icon: 'invoices', label: 'Invoices', name: 'Invoices & receipts', desc: 'Itemised invoices and sequential, gap-free receipts, sent as a PDF by SMS or email.' },
  { ic: 'ic-green', icon: 'rent', label: 'Payments', name: 'Rent & payments', desc: 'Charges raised each month; record cash, bank, cheque or M-Pesa and the balance follows on its own.' },
  { ic: 'ic-green', icon: 'mpesa', label: 'M-Pesa', name: 'M-Pesa collection', desc: 'STK push and paybill reconciliation, matched to the right tenant and invoice.' },
  { ic: 'ic-amber', icon: 'reports', label: 'Reports', name: 'Reports & KRA', desc: 'Twenty-one reports, including a Monthly Rental Income summary shaped for the KRA filing.' },
  { ic: 'ic-amber', icon: 'etims', label: 'eTIMS', name: 'eTIMS tax invoicing', desc: 'Electronic tax invoices transmitted to KRA for the rental tax regime, so a compliant invoice is a click, not a chore.', soon: true },
  { ic: 'ic-violet', icon: 'tenants', label: 'Tenants', name: 'Tenants & portal', desc: 'Every tenant a login of their own for balances, statements, documents and raising a repair.' },
  { ic: 'ic-blue', icon: 'owners', label: 'Owners', name: 'Owner portal', desc: 'Give each property owner a login for their own income, expenses and remittance, scoped to only the properties they own.' },
  { ic: 'ic-rose', icon: 'leases', label: 'Leases', name: 'Leases & inspections', desc: 'Lease documents with renewal reminders, and signed move-in and move-out inspections behind the deposit.' },
  { ic: 'ic-terra', icon: 'operations', label: 'Maintenance', name: 'Maintenance', desc: 'Tenants raise a request; you assign it, cost it and track it through to done, with the history kept.' },
  /*
   * There was an "AI Agents" node here. It went when the AI workforce section
   * was built, because the two said the same thing a screen apart — and the
   * section says it with fifteen named agents and their live status, which one
   * node on a wheel cannot. What is left is what this diagram is actually for:
   * the MODULES, and the fact that they run off one set of records.
   */
];

// Node positions on the wheel: evenly spaced from twelve o'clock, on a radius
// that leaves room for each node's label inside the square. Step is derived from
// the count so the ring stays even whether there are eight nodes or ten.
function hubPos(i: number, count: number, radius = 39): { x: number; y: number } {
  const ang = (-90 + i * (360 / count)) * (Math.PI / 180);
  return { x: 50 + radius * Math.cos(ang), y: 50 + radius * Math.sin(ang) };
}

/**
 * The calculator starts where the published packages start. The Free package
 * is hidden from the public price list (migration 142), so a figure below the
 * paid packages' first unit would quote a package the page does not offer.
 */
const CALC_MIN = 4;

export function Landing() {
  // Reachable at both "/" and "/home"; "/" is the one that counts.
  useCanonical('/');
  const [annual, setAnnual] = useState(false);
  const [calcUnits, setCalcUnits] = useState(25); // interactive pricing calculator
  const [hubActive, setHubActive] = useState(4); // connected-platform wheel: which node's panel shows (Reports)
  const [agent, setAgent] = useState<string | null>(null); // AI workforce: which agent's panel is open
  const open = WORKFORCE.find((c) => c.code === agent) ?? null;

  /* A dialog has to close on Escape, and the card that opened it is a button,
     so the pointer path and the keyboard path both end up here. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAgent(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /*
   * The wires from the core to each agent.
   *
   * Drawn rather than decorated: the line has to start at the edge of the hub
   * and end at the card wherever the grid happened to put it, and the grid
   * reflows at three breakpoints and again whenever the font renders wider. So
   * the geometry is measured from the DOM and redrawn on resize, which is the
   * only version of this that cannot drift out of alignment.
   *
   * The SVG is inert — absolutely positioned, aria-hidden, pointer-events none —
   * so it never intercepts a click meant for a card and never affects layout,
   * which is also what stops the ResizeObserver feeding itself.
   */
  const aiwRef = useRef<HTMLDivElement>(null);
  const [wires, setWires] = useState<{ x1: number; y1: number; x2: number; y2: number; cat: string }[]>([]);
  const [wireBox, setWireBox] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const host = aiwRef.current;
    if (!host) return;

    const draw = () => {
      const box = host.getBoundingClientRect();
      const core = host.querySelector('.aiw-hub-core')?.getBoundingClientRect();
      setWireBox({ w: box.width, h: box.height });

      // Below the desktop breakpoint the hub is a banner rather than a centre,
      // so there is nothing sensible to draw a line from.
      if (!core || box.width < 1100) { setWires([]); return; }

      const cx = core.left + core.width / 2 - box.left;
      const cy = core.top + core.height / 2 - box.top;
      const edge = core.width / 2 + 4;

      setWires([...host.querySelectorAll<HTMLElement>('.aiw-card')].map((card) => {
        const r = card.getBoundingClientRect();
        const x2 = r.left + r.width / 2 - box.left;
        const y2 = r.top + r.height / 2 - box.top;
        const dx = x2 - cx;
        const dy = y2 - cy;
        const len = Math.hypot(dx, dy) || 1;
        return {
          x1: cx + (dx / len) * edge, y1: cy + (dy / len) * edge,
          x2, y2, cat: card.dataset.cat ?? 'property',
        };
      }));
    };

    /*
     * Four ways in, because any one of them alone leaves the wires stale.
     *
     * The first measurement happens before web fonts settle, so a frame later
     * and a beat after that catch the reflow. ResizeObserver is the right tool
     * for the container changing, but it is delivered on the rendering
     * lifecycle and a tab that is not painting never gets it — which is exactly
     * what happened while this was being built, and left the section wired to
     * nothing. window.resize is the coarse backstop that fires regardless.
     * transitionend covers a card growing under the pointer, which moves every
     * card in its row.
     */
    draw();
    const frame = requestAnimationFrame(draw);
    const settle = setTimeout(draw, 250);
    const ro = new ResizeObserver(draw);
    ro.observe(host);
    window.addEventListener('resize', draw);
    host.addEventListener('transitionend', draw);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
      ro.disconnect();
      window.removeEventListener('resize', draw);
      host.removeEventListener('transitionend', draw);
    };
  }, []);

  // Rates, bands and the annual discount are live configuration, never written
  // into the page — the published price and the billing engine read the same
  // rows. The fallback covers the instant before the response lands.
  const pricing = useApi<PricingResponse>('/api/v1/pricing');
  const plans = pricing.data?.plans ?? [];

  /*
   * Which package the calculator is quoting.
   *
   * Each metered package carries its own rate ladder — size sets the rate
   * inside a package, the package sets the ladder — so "what does it cost"
   * cannot be answered without saying which one. Standard is the default
   * because it is the entry paid package and the honest answer to an
   * unqualified "how much".
   */
  const metered = plans.filter(p => p.plan_kind === 'metered');
  const [calcPlan, setCalcPlan] = useState('standard');
  const chosen = metered.find(p => p.code === calcPlan) ?? metered[0];

  const model = chosen?.tiers?.length
    ? {
        ...(pricing.data?.model ?? FALLBACK_MODEL),
        tiers: chosen.tiers,
        minimumMonthly: chosen.minimumMonthly ?? 0,
      }
    : (pricing.data?.model.tiers.length ? pricing.data.model : FALLBACK_MODEL);

  const quote = quoteUnits(model, calcUnits);
  // Computed on the SERVER from the same ladder, so these examples and the
  // calculator above them cannot quietly drift apart.
  const examples = chosen?.examples ?? pricing.data?.examples ?? [];

  /*
   * React Router does not scroll to a #hash on its own, so the header's
   * "Pricing" link and the footer's How-it-works / Questions links would
   * otherwise change the URL and do nothing.
   *
   * This was a 60ms setTimeout, and on THIS page the guess lost: Find Your
   * Next Home sits directly above #pricing and fills from a fetch, so the
   * target was still moving when the scroll fired and Pricing landed on the
   * rentals search instead. See useHashScroll for the rest of it.
   */
  useHashScroll();

  return (
    <div className="lp">
      <PublicHeader />

      {/* ---- hero ---------------------------------------------------------- */}
      <section className="lp-hero">
        <HeroMedia src="/hero-building.webp" video="/hero-loop.mp4"
          alt="A modern apartment building in Nairobi at golden hour" />
        <div className="lp-hero-scrim" />
        {GLOW_ENABLED && <HeroGlow />}
        <div className="lp-hero-inner">
          <span className="lp-eyebrow">NYUMBASUITE | INTELLIGENT PROPERTY OPERATIONS</span>
          <h1 className="lp-h1">Property management that thinks ahead.</h1>
          {/* The deck states the shift the headline only implies. One line, and
              deliberately between the h1 and the paragraph in both size and
              weight, so the eye lands headline → deck → detail. */}
          <p className="lp-hero-deck">From Property Management to Property Intelligence.</p>
          <p className="lp-lede">
            AI agents, predictive intelligence and automation that help you understand your
            properties, anticipate problems and make better decisions, across Kenya and
            East Africa.
          </p>
          {/* The capability line: a label for what the product IS, not a third
              sentence. The separators are decorative, so they are hidden from a
              screen reader, which then reads the three capabilities as a list of
              phrases rather than "bullet" three times. */}
          <p className="lp-hero-caps">
            <span>AI Agents</span>
            <span aria-hidden="true">•</span>
            <span>Predictive Intelligence</span>
            <span aria-hidden="true">•</span>
            <span>Automation</span>
          </p>
          <div className="lp-cta">
            <Link className="btn btn-invert" to="/register">Get started, no card needed</Link>
            <a className="btn btn-outline-invert" href="#pricing">See pricing</a>
          </div>
          {/* The hero sells to landlords, but the platform serves three other
              people too — a renter, a seller, a service business — and none of
              them should have to guess. A quiet stack of prompts, well below the
              landlord CTAs so they do not compete, each pointing at the same
              page its Explore section does. */}
          <div className="lp-hero-links">
            <p className="lp-hero-tenant">
              <span className="lp-hero-q">Looking to rent? </span><Link to="/homes">Find your next home →</Link>
            </p>
            <p className="lp-hero-tenant">
              <span className="lp-hero-q">Got something to sell? </span><Link to="/marketplace/post">Sell something →</Link>
            </p>
            <p className="lp-hero-tenant">
              <span className="lp-hero-q">Offer a service? </span><Link to="/services/register">List your business →</Link>
            </p>
            {/* A scroll to the footer's contact block, which holds the phone and
                the support mailbox. A plain in-page anchor, not a route: it moves
                the reader down the same landing page rather than navigating. */}
            <p className="lp-hero-tenant">
              <span className="lp-hero-q">Have a question? </span><a href="#contact">Contact us →</a>
            </p>
          </div>
          <p className="lp-hero-note">Set up in minutes. Pay by M-Pesa. Works on your phone.</p>
          <HeroTicker />
        </div>
      </section>

      {/* ---- the product, moving: real screens, not drawn ones -------------- */}
      <section className="lp-strip" aria-label="NyumbaSuite screens">
        <Marquee items={STRIP} seconds={70} label="NyumbaSuite screens, moving" render={(it) => (
          <figure className="lp-shot">
            <div className="lp-shot-frame">
              <img src={it.src} alt={it.alt} width={1208} height={755} loading="lazy" decoding="async" />
            </div>
            <figcaption>{it.label}</figcaption>
          </figure>
        )} />
        <p className="lp-preview-note">Real screens from the product, with demo data. Property names, tenant names and phone numbers are hidden.</p>
      </section>

      {/* ---- problem → the platform watches with you -----------------------
          Text on the left, a quiet "attention" card on the right that shows the
          KINDS of things the platform surfaces. The alerts are labels, not
          fabricated account figures, so the card reads as an illustration of
          what is watched rather than a claim about a specific portfolio. */}
      {/* ---- the question the next section answers --------------------------
          A "Watching your portfolio" panel stood on the right of this, carrying
          four invented alerts tagged Collections, Leases, Operations and
          Intelligence. It went for two reasons, both of which this page had
          already settled elsewhere.

          It was a picture we drew of the product. That is the exact argument
          HOME_TOUR makes at the top of this file for using real screenshots —
          "it asked a visitor to take our word for what the software looks like
          on the strength of a picture we drew of it" — and the same reason the
          workforce cards carry no invented occupancy figure.

          And its four tags were four of the teams standing immediately below it,
          so a visitor met the same categories twice in one scroll, once as
          fiction and once for real. What is left is the question, stated once
          and answered by the roster underneath: the setup for the section, not a
          rehearsal of it. */}
      <section className="lp-section" id="problem">
        <div className="lp-section-inner">
          <div className="lp-head-center">
            <h2 className="lp-h2">Your properties should not need you to watch everything.</h2>
            <p className="lp-sub">
              NyumbaSuite watches your property operations, identifies what needs attention and
              helps you act before small issues become expensive problems.
            </p>
          </div>
          <WatchConsole />
        </div>
      </section>

      {/* ---- the AI workforce ----------------------------------------------
          Was six cards describing six "areas of intelligence". The areas were
          true but they were not the product: what runs is a roster of named
          agents, each one a bundle of detectors, skills and the screens they
          point at. Showing the roster is showing the thing.

          THE STATUS DOTS ARE NOT COPY. Every name, and every Active/Coming soon
          beside it, is checked against src/domain/agent/agents.ts by
          tests/aiworkforce.test.ts, which fails the build if this page and the
          product disagree. That matters in one direction especially: a
          hand-kept list of fourteen statuses on a marketing page drifts within a
          fortnight, and it drifts flatteringly. The roster's own header states
          the rule — "a name with nothing behind it is a claim, and the house
          rule is that a claim gets a marker".

          Every card here is running on a real account tonight — AI
          Communications was the last one carrying a marker and earned its way
          off on 2026-09-13, when live gained the ability to actually send. The
          Coming-soon branch stays in this renderer regardless: a page with no
          way left to say "not yet" is a page that will start claiming things.

          No figures are shown on the cards: an invented "96% occupancy" on a
          public page reads as the visitor's own portfolio, and this section is
          about what the product watches, not about numbers nobody can check. */}
      <section className="lp-section tint" id="intelligence">
        <div className="lp-section-inner">
          <div className="lp-head-center">
            <p className="aiw-eyebrow">Meet the NyumbaSuite AI workforce</p>
            <h2 className="lp-h2">
              {AGENT_COUNT} AI agents and 1 AI assistant.<br />
              One intelligent property business.
            </h2>
            <p className="lp-sub">
              NyumbaSuite brings together a team of specialised AI agents that continuously
              monitor, understand and help operate your property portfolio — from collections
              and finance to maintenance, leases, tenants and business performance.
            </p>
          </div>

          <PortfolioFilm poster="/cinematic/estate.jpg" video="/cinematic/portfolio.mp4"
            lines={['Every property', 'Every unit', 'Every tenant', 'Rent in, receipts out', 'Repairs tracked', 'Leases watched', 'Owners informed']} />

          <div className="aiw" ref={aiwRef}>
            {/* Every agent wired back to the core, tinted by the part of the
                business it works in. Measured in the effect above. */}
            <svg
              className="aiw-wires" aria-hidden
              width={wireBox.w} height={wireBox.h}
              viewBox={`0 0 ${wireBox.w || 1} ${wireBox.h || 1}`}
            >
              {wires.map((w, i) => (
                <line
                  key={i} className={`aiw-wire aiw-${w.cat}`}
                  x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2}
                  style={{ animationDelay: `${(i % 5) * 0.6}s` }}
                />
              ))}
            </svg>

            {/* The core the others report into. Decorative: the cards carry the
                meaning, so this is hidden from assistive technology. */}
            <div className="aiw-hub" aria-hidden>
              <span className="aiw-orbit aiw-orbit-1" />
              <span className="aiw-orbit aiw-orbit-2" />
              <span className="aiw-orbit aiw-orbit-3" />
              <div className="aiw-hub-core">
                <img
                  src="/brand-mark-64.png?v=2" alt=""
                  className="aiw-hub-logo" width={56} height={56}
                />
                <strong>NyumbaSuite AI</strong>
                <span className="aiw-hub-sub">Property intelligence</span>
                <span className="aiw-hub-meta">Data · Insights · Automation</span>
              </div>
            </div>

            {/* Five teams, each named once, rather than fifteen cards each
                repeating its own label and a legend repeating them again. */}
            {(['left', 'right'] as const).map((side) => (
              <div className={`aiw-side aiw-side-${side}`} key={side}>
                {COLUMNS[side].map((cat) => (
                  <section className={`aiw-team aiw-${cat}`} key={cat}>
                    <h3 className="aiw-team-head">
                      <span className="aiw-dot" aria-hidden />
                      {CATEGORY_LABEL[cat]}
                      <span className="aiw-team-count">{agentsIn(cat).length}</span>
                    </h3>
                    <div className="aiw-team-cards">
                      {agentsIn(cat).map((card) => (
                        <button
                          key={card.code}
                          type="button"
                          className="aiw-card"
                          data-cat={card.category}
                          onClick={() => setAgent(card.code)}
                          aria-haspopup="dialog"
                        >
                          <span className="aiw-glyph" aria-hidden>{card.glyph}</span>
                          <span className="aiw-name">{card.name}</span>
                          <span className="aiw-short">{card.short}</span>
                          <span className={`aiw-state ${card.live ? 'is-live' : 'is-soon'}`}>
                            {card.live ? 'Active' : 'Coming soon'}
                          </span>
                          {/* Revealed on hover and on keyboard focus, so the
                              detail is reachable without a pointer. */}
                          <span className="aiw-actions">
                            <span className="aiw-actions-in">
                              {card.actions.map((a) => <span key={a}>{a}</span>)}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            ))}
          </div>

          <p className="aiw-note">
            One platform. {AGENT_COUNT} specialised AI agents. One intelligent property workforce.
          </p>
          <div className="aiw-cta">
            <Link className="btn btn-primary" to="/solutions#ai-assistant">Explore the AI workforce</Link>
          </div>
        </div>

        {open && (
          <div
            className="aiw-modal"
            role="dialog"
            aria-modal="true"
            aria-label={open.name}
            onClick={() => setAgent(null)}
          >
            <div className="aiw-panel" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="aiw-close" onClick={() => setAgent(null)} aria-label="Close">×</button>
              <span className={`aiw-glyph aiw-${open.category}`} aria-hidden>{open.glyph}</span>
              <h3>{open.name}</h3>
              <span className={`aiw-state ${open.live ? 'is-live' : 'is-soon'}`}>
                {open.live ? 'Active' : 'Coming soon'}
              </span>
              <dl>
                <dt>What it monitors</dt>
                <dd>{open.monitors}</dd>
                <dt>What it understands</dt>
                <dd>{open.understands}</dd>
                <dt>Example questions</dt>
                <dd>
                  <ul className="aiw-ex">
                    {open.actions.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                </dd>
              </dl>
            </div>
          </div>
        )}
      </section>

      {/* ---- the tour: one sentence and the real screen, per tab -------------
          Replaces the six-screen slideshow and the nine paragraph cards. A visitor
          picks what they care about and sees it, instead of reading about it. */}
      <section className="lp-section tint" id="proof">
        <div className="lp-head-center">
          <h2 className="lp-h2">See it working</h2>
          <p className="lp-sub">Pick a part of the job. This is the screen you would use.</p>
        </div>
        <ProductTour stops={TOUR} />
        <p className="lp-preview-note">Demo data. Property names, tenant names and phone numbers are hidden on every screen.</p>
        <p className="lp-preview-note">
          There is more than fits here: utilities, expenses, listings, bulk SMS and a guided
          import from your spreadsheet. <Link to="/about#platform">See everything in the platform</Link>.
        </p>
      </section>

      {/* ---- connected platform (the wheel) --------------------------------
          The whole product turning around one core rather than sitting in seven
          tools that do not talk. The capability grid ABOVE lists each module in
          full; this is the map of how they join up. Point at a node (or a row)
          and the panel and the spoke follow. See HUB for the honesty rule on
          which nodes appear.

          MOVED HERE from directly under the AI workforce section, which put two
          hub-and-spoke diagrams one after the other — the same picture twice,
          arguing two different things, which is what made the page feel
          repetitive however good either one was on its own. Now the AI roster
          opens, the screens and the capability grid carry the middle, and this
          closes by showing the modules share one set of records. */}
      <section className="lp-section tint" id="connected">
        <div className="lp-head-center">
          <h2 className="lp-h2">One connected platform</h2>
          <p className="lp-sub">
            Every part of the job turns around one core, so rent coming in, bills going out and
            everyone in the loop stay in step, instead of living in separate spreadsheets.
          </p>
        </div>

        <div className="lp-hub">
          <div className="lp-hub-wheel">
            <svg className="lp-hub-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <circle className="lp-hub-ring" cx="50" cy="50" r="39" />
              <circle className="lp-hub-ring" cx="50" cy="50" r="26" />
              {(() => { const p = hubPos(hubActive, HUB.length); return (
                <line className="lp-hub-line" x1="50" y1="50" x2={p.x} y2={p.y} />
              ); })()}
            </svg>
            <div className="lp-hub-core" aria-hidden="true">
              <img src="/brand-mark-64.png?v=2" alt="" className="lp-hub-core-logo" width={40} height={40} />
            </div>
            {HUB.map((h, i) => {
              const p = hubPos(i, HUB.length);
              return (
                <button key={h.name} type="button"
                  className={`lp-hub-node ${h.ic}${i === hubActive ? ' active' : ''}${h.soon ? ' soon' : ''}`}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                  aria-label={h.soon ? `${h.name} (coming soon)` : h.name} aria-pressed={i === hubActive}
                  onMouseEnter={() => setHubActive(i)} onFocus={() => setHubActive(i)}
                  onClick={() => setHubActive(i)}>
                  <span className="lp-hub-node-ic" aria-hidden="true"><SbIcon name={h.icon} size={h.soon ? 22 : 24} /></span>
                  <span className="lp-hub-node-label">{h.label}</span>
                </button>
              );
            })}
          </div>

          <ul className="lp-hub-list">
            {HUB.map((h, i) => (
              <li key={h.name}>
                <button type="button"
                  className={`lp-hub-item${i === hubActive ? ' active' : ''}`}
                  onMouseEnter={() => setHubActive(i)} onFocus={() => setHubActive(i)}
                  onClick={() => setHubActive(i)}>
                  <span className={`lp-hub-item-ic ${h.ic}`} aria-hidden="true"><SbIcon name={h.icon} size={h.soon ? 22 : 24} /></span>
                  <span className="lp-hub-item-text">
                    <span className="lp-hub-item-name">
                      {h.name}
                      {h.soon && <span className="pr-soon-tag">Coming soon</span>}
                    </span>
                    <span className="lp-hub-item-desc">{h.desc}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <p className="lp-preview-note">
          These modules run today and share one ledger — eTIMS tax invoicing is on the way.
          Your data flows between them and stays yours, isolated from every other landlord by
          the database itself.
        </p>
      </section>

      {/* ---- homes on the platform: a moving strip and four short facts -------
          Replaces the two photo-and-checklist sections (phone, and one property
          or twenty). Their claims survive as the four facts underneath. */}
      <section className="lp-section" id="homes">
        <div className="lp-head-center">
          <h2 className="lp-h2">From one flat to twenty buildings</h2>
          <p className="lp-sub">Apartments, estates and gated compounds, on one phone-friendly platform.</p>
        </div>
        <Marquee items={PLACES} seconds={80} label="Kinds of property managed on NyumbaSuite" className="mq-places" render={(it) => (
          <figure className="lp-place">
            <img src={it.src} alt={it.alt} width={640} height={420} loading="lazy" decoding="async" />
            <figcaption><strong>{it.label}</strong><span>{it.sub}</span></figcaption>
          </figure>
        )} />
        <ul className="lp-facts">
          <li><span className="lp-fact-ic" aria-hidden="true"><SbIcon name="paymentsin" size={20} /></span>Record a payment in under a minute</li>
          <li><span className="lp-fact-ic" aria-hidden="true"><SbIcon name="mpesa" size={20} /></span>Works on any phone, installs like an app</li>
          <li><span className="lp-fact-ic" aria-hidden="true"><SbIcon name="owners" size={20} /></span>Separate logins for managers, finance and staff</li>
          <li><span className="lp-fact-ic" aria-hidden="true"><SbIcon name="check" size={20} /></span>Your data is yours alone</li>
        </ul>
      </section>

      {/* ---- data to intelligence -------------------------------------------
          The same seven tenants, read two ways: what a report hands you, and
          what the product already does with it — banded by age, oldest first,
          the uncontactable one flagged rather than silently skipped, and one
          action that reminds all seven with their own balance. Every claim in
          the right-hand panel is a behaviour that exists today.

          The not-yet part is kept in its own strip underneath rather than
          blended into the panel. That separation is the whole point: the force
          of this comparison depends on the right-hand side being true, and one
          invented line in it would cost more than the line is worth.

          Figures are illustrative and say so, in the same register as the
          "demo portfolio" note under the screenshots. */}
      <section className="lp-section" id="answers">
        <div className="lp-head-center">
          <h2 className="lp-h2">Your software should not just give you data. It should give you answers.</h2>
          <p className="lp-sub">
            Seven tenants are behind on rent. This is the difference between being told that
            and being able to do something about it.
          </p>
        </div>

        <ArrearsAnswer />

        {/* The two findings that sat here as "being built" are both live: the
            balance that has grown three months running (collections.balance_rising,
            reconstructed month by month from payment dates) and the order to work
            the arrears in (the work list on the Arrears page, ranked on age,
            amount and what the collections agent holds open). */}
        <p className="lp-preview-note">Figures here are an illustration, not a real portfolio.</p>
      </section>

      {/* The "Most software waits to be asked" comparison stood here until
          2026-09-12. Its own header recorded that it had already been rewritten
          once for arguing the same point as its neighbour; the AI workforce
          section above made it redundant a second time, and more plainly —
          "every lease is watched from 90 days out" is one of its six bullets and
          is now a named agent with a status beside it. Showing the roster beats
          describing its behaviour in prose two screens later. */}

      {/* ---- Find Your Next Home lived here --------------------------------
          Removed 2026-09-19. It served a tenant who had arrived looking for
          somewhere to live, and the argument for it was that a tenant should
          not be stranded on a landlord's page. They are not: there are three
          routes to /homes from here already - the sticky header's "Find a
          home", the quiet prompt under the hero ("Looking to rent? Find your
          next home"), and the footer. This was the fourth, and by far the
          most expensive: eleven hundred pixels of a page already seventeen
          thousand long, a second search box competing with the one on the
          page it linked to, and a fetch on every visit.

          What decided it was what it had started SAYING. The strip shows
          whatever /discover/featured returns, taken adverts included, so on
          live it was three homes all badged "Taken" - the only live content
          on the front page, advertising that nothing is available. A section
          meant to show the marketplace is alive was showing the opposite.

          Its landlord CTA ("Have a vacant unit?") is not lost: /register is
          still linked from the hero and from the closing band. */}

      {/* ---- pricing: pay for the units you manage -------------------------- */}
      <section className="lp-section tint" id="pricing">
        <div className="lp-section-inner">
          <div className="lp-head-center">
            <h2 className="lp-h2">Pay only for the units you manage.</h2>
            <p className="lp-sub">
              Every package takes any portfolio, so you choose the package for what it does, and
              your size sets the rate inside it. Each unit is charged at the rate of its own band,
              so crossing a threshold never reprices the units below it.
            </p>
            <div className="lp-toggle" role="group" aria-label="Billing period">
              <button className={annual ? '' : 'active'} onClick={() => setAnnual(false)}>Monthly</button>
              <button className={annual ? 'active' : ''} onClick={() => setAnnual(true)}>Annual</button>
            </div>

            {/* Which package the figures below describe. Every package serves
                every portfolio size now, so this is a real choice rather than
                a consequence of how many units somebody has. */}
            {metered.length > 1 && (
              /* Captioned, because two unlabelled pill toggles stacked on top
                 of each other read as one control with six settings — the
                 first reader of this page switched nothing, then reported the
                 figures had not changed. */
              <div className="pr-pkg">
                <span className="pr-pkg-label">Price it on</span>
                <div className="lp-toggle" role="group" aria-label="Package">
                  {metered.map(p => (
                    <button key={p.code}
                      className={calcPlan === p.code ? 'active' : ''}
                      onClick={() => setCalcPlan(p.code)}>{p.name}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* interactive calculator + rate matrix */}
          <div className="pr-calc-wrap">
            <div className="pr-calc">
              <div className="pr-calc-q">How many units do you manage?</div>
              <div className="pr-calc-control">
                <button type="button" aria-label="Fewer units" onClick={() => setCalcUnits((u) => Math.max(CALC_MIN, u - 1))}>−</button>
                <input type="number" min={CALC_MIN} aria-label="Units managed" value={calcUnits}
                  onChange={(e) => setCalcUnits(Math.max(CALC_MIN, Math.min(100000, Number(e.target.value) || 0)))} />
                <button type="button" aria-label="More units" onClick={() => setCalcUnits((u) => u + 1)}>+</button>
              </div>
              <div className="pr-calc-out">
                <span className="pr-calc-label">
                  Your {annual ? 'annual' : 'monthly'} subscription{chosen ? ` on ${chosen.name}` : ''}
                </span>
                <span className="pr-calc-price">
                  {quote.free ? 'Free' : formatKES(annual ? quote.annual : quote.monthly)}
                  {!quote.free && <small>/{annual ? 'year' : 'month'}</small>}
                </span>
                {/* The sum, not just the total: showing the working is what makes
                    "progressive" a fact the visitor can check rather than a claim. */}
                {/* The working, and the floor when the floor is what decided
                    the figure. Printing "20 × 250 = KES 15,000" because the
                    minimum lifted the total is arithmetic a reader can check
                    and find wrong, which costs more trust than the floor
                    itself ever earns. */}
                <span className="pr-calc-bands">
                  {quote.free
                    ? `Up to ${model.freeUnits} units is free, for ever.`
                    : quote.minimumApplied
                      ? `${bandSum(quote)} = ${formatKES(bandTotal(quote))}, under the ${chosen?.name ?? ''} minimum of ${formatKES(quote.monthly)}/month`
                      : `${bandSum(quote)} = ${formatKES(quote.monthly)}/month`}
                </span>
                {!quote.free && annual && quote.annualSaving > 0 && (
                  <span className="pr-calc-save">
                    {formatKES(quote.monthly)}/month × 12, less {Math.round(model.annualDiscountPercent)}%,
                    so you keep {formatKES(quote.annualSaving)}
                  </span>
                )}
                <span className="pr-calc-note">
                  Progressive pricing, so you get better rates as your portfolio grows. Units in lower
                  bands keep their own rate.
                </span>
              </div>
            </div>

            <div className="pr-rate">
              <div className="pr-rate-title">
                How the price is built{chosen ? `: ${chosen.name}` : ''}
              </div>
              <table className="pr-rate-table">
                <tbody>
                  {rateRows(model).map((r) => (
                    <tr key={r.range}><td>{r.range}</td><td>{r.rate}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="pr-rate-ex">
                {examples.filter(e => !e.free).map((e) => (
                  <div key={e.units}>
                    <strong>{e.units} units</strong> = {bandSum(e)} = {e.minimumApplied
                      ? <>{formatKES(bandTotal(e))}, so the{' '}
                          <strong>{formatKES(e.monthly)}/mo</strong> minimum applies</>
                      : <strong>{formatKES(e.monthly)}/mo</strong>}
                  </div>
                ))}
              </div>
              <div className="pr-rate-note">
                Never a cliff: at {model.tiers[0]?.upTo ?? 0} units you pay{' '}
                {formatKES(quoteUnits(model, model.tiers[0]?.upTo ?? 0).monthly)}, and the next unit
                adds only {formatKES(model.tiers[1]?.rate ?? 0)}, not a re-priced portfolio.
                {(chosen?.minimumMonthly ?? 0) > 0 && (
                  <> {chosen!.name} starts at {formatKES(chosen!.minimumMonthly!)}/month, so a
                  smaller portfolio can still take it.</>
                )}
              </div>
            </div>
          </div>

          {/* colour-coded plan cards, from live plan configuration */}
          <div className="pr-plans">
            {plans.map((p) => {
              const custom = p.plan_kind === 'custom';
              const free = p.plan_kind === 'free';
              return (
                <div key={p.code} className="pr-plan"
                  /* `||`, not `??`: an operator who clears the accent colour on
                     System settings stores an empty string, and ?? would hand
                     that straight to CSS as `--accent: `, leaving the card with
                     no trim and no fallback either. Empty means unset here. */
                  style={{ '--accent': p.accent_color || '#2563EB' } as CSSProperties}>
                  <div className="pr-plan-name">{p.name}</div>
                  <div className="pr-plan-for">{p.audience}</div>
                  {/* Not a size band any more. Every paid package serves any
                      portfolio, so the chip says where the charge starts and,
                      where there is one, the floor — which is what makes
                      "fifty units on Enterprise" a real and priced answer
                      rather than a conversation. */}
                  <div className="pr-plan-units">
                    {custom ? 'Any portfolio size'
                      : free ? bandLabel(p.unit_min, p.unit_max)
                      : (p.minimumMonthly ?? 0) > 0
                        ? `Any size · from ${formatKES(p.minimumMonthly!)}/mo`
                        : 'Any portfolio size'}
                  </div>
                  {/* "From", because billing is progressive: this is the rate the
                      band starts at, not a figure the whole portfolio pays. */}
                  <div className="pr-plan-price">
                    {custom ? 'Negotiated' : free ? formatKES(0) : `From ${formatKES(p.fromRate ?? 0)}`}
                  </div>
                  <div className="pr-plan-unit">
                    {custom ? 'quoted around what you need'
                      : free ? 'for ever'
                      : p.unit_min > 1 ? 'per additional unit / month' : 'per unit / month'}
                  </div>
                  {/* The bands, on the card that charges them.
                      "From KES 150" is true and unreadable on its own: it is
                      the rate of the FIRST band, and nothing told a reader the
                      200th unit is cheaper. Three lines per package also make
                      both axes visible at a glance — rates fall down a column
                      as the portfolio grows, and rise across the row as the
                      package does. */}
                  {!custom && !free && (p.tiers?.length ?? 0) > 0 && (
                    <div className="pr-plan-rates">
                      <div className="pr-plan-rates-h">Rate per unit</div>
                      <table>
                        <tbody>
                          {planLadder(p.tiers, p.unit_min).map((r) => (
                            <tr key={r.range}>
                              <td>{r.range} units</td><td>{r.rate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* A roadmap line is marked as one. Selling M-Pesa
                      reconciliation or WhatsApp delivery unqualified would be
                      selling a landlord something they will go looking for on
                      their first rent day. */}
                  <ul className="pr-plan-features">
                    {p.highlights.map((h) => {
                      const soon = h.startsWith('Coming soon:');
                      return (
                        <li key={h} className={soon ? 'pr-soon' : undefined}>
                          {soon ? h.replace('Coming soon:', '').trim() : h}
                          {soon && <span className="pr-soon-tag">Coming soon</span>}
                        </li>
                      );
                    })}
                  </ul>
                  <Link className="btn btn-block pr-plan-cta" to={custom ? '/quote' : '/register'}>
                    {/* `||` for the reason the accent uses it: a cleared label
                        is an empty string, and ?? would render a button with no
                        text on it. */}
                    {p.cta_label || 'Get started'}
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="pr-note-mid">
            <strong>Any package, any size.</strong> The package decides what you get; your portfolio
            decides the rate inside it, and rates fall as you grow. A fifty-unit landlord can take
            Enterprise, and a six-hundred-unit one can stay on Standard.{' '}
            <strong>Need more flexibility?</strong> Custom covers data migration, done-for-you
            setup, custom reports, integrations or a fully managed service.{' '}
            <Link to="/quote">Ask us for a quotation</Link>.
          </p>
          <p className="pr-fineprint">
            {model.billableBasis === 'occupied'
              ? <>We bill the units you have <strong>occupied</strong>, so a vacant unit attracts no
                 recurring charge. </>
              : <>We bill <strong>every unit you manage</strong>, occupied or vacant, so the figure
                 above is the figure you pay. Units you archive drop out. </>}
            Every unit is charged at the rate of its band, from the first unit.
            SMS allowances are included in each plan, with extra SMS available separately. Any
            M-Pesa or bank charges are separate from your NyumbaSuite subscription.
          </p>
        </div>
      </section>

      {/* ---- FAQ ------------------------------------------------------------ */}
      <section className="lp-section" id="faq">
        <div className="lp-head-center">
          <h2 className="lp-h2">Questions landlords ask</h2>
        </div>
        <div className="lp-faq">
          <details>
            <summary>Do I need a card to start?</summary>
            <p>No. Create your account, add your properties and see NyumbaSuite working on your own portfolio. When you choose a package you pay by M-Pesa, monthly or annually.</p>
          </details>
          <details>
            <summary>What happens when I add more units than my plan allows?</summary>
            <p>We tell you before anything is blocked, what you are using, which plan covers it, and what that plan costs. Nothing is deleted and no unit disappears.</p>
          </details>
          <details>
            <summary>Can I move to a smaller plan later?</summary>
            <p>Yes, as long as your usage fits inside it. If you have more units than the smaller plan allows, we say exactly how many you would need to remove first rather than silently dropping them.</p>
          </details>
          <details>
            <summary>Does it handle part payments and money paid in advance?</summary>
            <p>Yes. A part payment reduces the balance and leaves the rest outstanding. Money paid over what is owed is held as credit against the next month rather than being counted as collected rent.</p>
          </details>
          <details>
            <summary>Can my caretaker or accountant use it too?</summary>
            <p>Yes. Invite them with their own login and a role that limits what they can see and do. A finance user need not see maintenance, and staff need not see money.</p>
          </details>
          <details>
            <summary>Is my data separate from other landlords?</summary>
            <p>Yes, and it is enforced by the database itself rather than by application code. A query that forgets which account it belongs to returns nothing at all.</p>
          </details>
        </div>
      </section>

      {/* ---- final CTA ------------------------------------------------------ */}
      <section className="lp-section tint lp-final">
        <div className="lp-section-inner">
          <h2 className="lp-h2">Start with one property</h2>
          <p>
            Add a property, add your units, and record your first payment. It takes a few minutes,
            and you choose the package that fits when you are ready.
          </p>
          <div className="lp-cta" style={{ justifyContent: 'center' }}>
            <Link className="btn btn-invert" to="/register">Create your account</Link>
          </div>
        </div>
      </section>

      {/* ---- need help — compact, deliberately not a full CTA band --------- */}
      <section className="lp-section lp-help">
        <div className="lp-help-inner">
          <div className="lp-help-text">
            <h2 className="lp-help-title">Need help? Get in touch</h2>
            <p>Talk to us directly: call, WhatsApp, or book a walkthrough.</p>
          </div>
          <div className="lp-help-actions">
            <a className="lp-help-phone" href={CONTACT.primaryTel}>{CONTACT.primaryPhone}</a>
            <a className="btn btn-primary btn-sm" href={CONTACT.primaryTel}>Call us</a>
            <a className="btn btn-secondary btn-sm" href={CONTACT.whatsapp} target="_blank" rel="noreferrer">WhatsApp us</a>
            <Link className="btn btn-secondary btn-sm" to="/demo">Request a demo</Link>
            {/* The brochure (a PDF uploaded from the Control Centre and served
                from the database) opens in a new tab to read. */}
            <a className="btn btn-secondary btn-sm" href="/nyumbasuite-user-guide.pdf" target="_blank" rel="noreferrer">Read our brochure</a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
